/**
 * CmsLayout -- Layout wrapper for all CMS pages.
 *
 * Provides a sidebar with CMS navigation alongside the existing app.
 * Uses the same dashboard sidebar pattern as DashboardPage.
 */
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore, useAuthActions } from '../../state/useAuthStore';
import { Button } from '../ui/Button';

export function CmsLayout() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { logout } = useAuthActions();

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div className="dashboard">
      {/* ---------- Sidebar ---------- */}
      <aside className="dashboard__sidebar">
        <div className="sidebar__brand">
          <h2 className="sidebar__brand-name">MyManyResumes</h2>
        </div>

        <div className="sidebar__user">
          <div className="sidebar__avatar" aria-hidden="true">
            {user?.name?.charAt(0)?.toUpperCase() ?? user?.email?.charAt(0)?.toUpperCase() ?? '?'}
          </div>
          <p className="sidebar__user-name">{user?.name ?? 'User'}</p>
          <p className="sidebar__user-email">{user?.email ?? ''}</p>
        </div>

        <nav className="sidebar__nav sidebar__nav--cms">
          <div className="sidebar__nav-section">
            <span className="sidebar__nav-heading">Resumes</span>
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Dashboard
            </NavLink>
          </div>

          <div className="sidebar__nav-section">
            <span className="sidebar__nav-heading">CMS</span>
            <NavLink
              to="/cms/sites"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Sites
            </NavLink>
            <NavLink
              to="/cms/templates"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Templates
            </NavLink>
          </div>

          <div className="sidebar__nav-section">
            <span className="sidebar__nav-heading">AI Tools</span>
            <NavLink
              to="/cms/tools/score-resume"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Score Resume
            </NavLink>
            <NavLink
              to="/cms/tools/cover-letter"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Cover Letter
            </NavLink>
            <NavLink
              to="/cms/tools/parse-job"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Parse Job Description
            </NavLink>
            <NavLink
              to="/cms/tools/job-descriptions"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Saved Job Descriptions
            </NavLink>
          </div>

          <div className="sidebar__nav-section">
            <span className="sidebar__nav-heading">Content Library</span>
            <NavLink
              to="/cms/content/personal-info"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Personal Info
            </NavLink>
            <NavLink
              to="/cms/content/career"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Career
            </NavLink>
            <NavLink
              to="/cms/content/skills"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Skills
            </NavLink>
            <NavLink
              to="/cms/content/education"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Education
            </NavLink>
            <NavLink
              to="/cms/content/certificates"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Certificates
            </NavLink>
            <NavLink
              to="/cms/content/patents"
              className={({ isActive }) =>
                `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
              }
            >
              Patents
            </NavLink>
          </div>

          <div className="sidebar__nav-bottom">
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Sign Out
            </Button>
          </div>
        </nav>
      </aside>

      {/* ---------- Main content ---------- */}
      <main className="dashboard__main">
        <Outlet />
      </main>
    </div>
  );
}
